import { useState, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import { TONES, EDITABLE_MESSAGES, MESSAGE_MAX_LENGTH, defaultMessage, previewMessage } from 'services/Whatsapp'
import MainButton from 'components/Buttons/MainButton'

import {
  StyleLayout,
  Card,
  CardTitle,
  Caption,
  Row,
  Field,
  Hint,
  LinkButton,
  ModeButton,
  TextArea,
  Phone,
  Bubble,
  ErrorText
} from './styles'
import CheckBox from 'components/CheckBox'

const aliasesText = list => (list ?? []).join(', ')
const parseAliases = text => text.split(',').map(a => a.trim()).filter(Boolean)

/*
  O "Jeito de atender": tom, nome da assistente, emoji, mensagens próprias e apelidos dos
  serviços, com a prévia da conversa ao lado. Vale na próxima mensagem que o bot responder.
  Valor e link do sinal não são editáveis e não aparecem aqui.
*/
export default function Style({ businessId, salonName }) {
  const [style, setStyle] = useState(null)
  const [services, setServices] = useState([])
  const [aliases, setAliases] = useState({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [errors, setErrors] = useState([])

  const url = `/businesses/${businessId}/whatsapp/style`

  const apply = data => {
    setStyle({ tone: data.tone, assistant_name: data.assistant_name ?? '', emojis: !!data.emojis, messages: data.messages ?? {} })
    setServices(data.services ?? [])
    setAliases(Object.fromEntries((data.services ?? []).map(s => [s.id, aliasesText(data.service_aliases?.[s.id])])))
  }

  useEffect(() => {
    cutileiApi.get(url)
      .then(({ data }) => apply(data))
      .catch(err => {
        logApiError(err, 'whatsapp.style')
        setErrors([getApiErrorMessage(err, 'Não foi possível carregar o jeito de atender.')])
      })
  }, [url])

  if (!style) return errors.length ? <ErrorText>{errors[0]}</ErrorText> : <Caption>Carregando...</Caption>

  const change = patch => {
    setStyle(current => ({ ...current, ...patch }))
    setSaved(false)
  }
  const changeMessage = (key, value) => change({ messages: { ...style.messages, [key]: value } })

  const handleSave = async () => {
    setSaving(true)
    setErrors([])
    try {
      const { data } = await cutileiApi.put(url, {
        ...style,
        assistant_name: style.assistant_name.trim() || null,
        service_aliases: Object.fromEntries(Object.entries(aliases).map(([id, text]) => [id, parseAliases(text)]))
      })
      apply(data)
      setSaved(true)
    } catch (err) {
      logApiError(err, 'whatsapp.style')
      setErrors(err?.response?.data?.errors ?? [getApiErrorMessage(err, 'Não foi possível salvar.')])
    } finally {
      setSaving(false)
    }
  }

  const salon = salonName || 'Salão'
  const firstService = services[0]?.name?.toLowerCase() ?? 'escova'
  const preview = [
    { mine: false, text: 'oi, queria marcar um horário' },
    { mine: true, text: `${previewMessage(style, 'greeting', { salon })} ${previewMessage(style, 'ask_service', { salon })}` },
    { mine: false, text: firstService },
    { mine: true, text: previewMessage(style, 'ask_date', { salon, vars: { servico: firstService } }) },
    { mine: false, text: 'amanhã de tarde' },
    { mine: true, text: previewMessage(style, 'booking_handoff', { salon, vars: { pedido: `${firstService} amanhã de tarde` } }) }
  ]

  return (
    <StyleLayout>
      <Row style={{ flexDirection: 'column', alignItems: 'stretch', gap: 15 }}>
        <Card>
          <CardTitle>Tom e apresentação</CardTitle>
          <Row $gap={8} role='radiogroup' aria-label='Tom'>
            {TONES.map(tone => (
              <ModeButton
                key={tone.value}
                type='button'
                role='radio'
                aria-checked={style.tone === tone.value}
                $color='#FF9800'
                $selected={style.tone === tone.value}
                onClick={() => change({ tone: tone.value })}
              >
                {tone.label}
              </ModeButton>
            ))}
          </Row>
          <Field>
            Nome da assistente
            <input
              type='text'
              maxLength={40}
              placeholder='Ex.: Bia (vazio: o bot se apresenta como o salão)'
              value={style.assistant_name}
              onChange={e => change({ assistant_name: e.target.value })}
            />
          </Field>
          <Row as='label' $gap={8} style={{ fontSize: 13, cursor: 'pointer' }}>
            <CheckBox value={style.emojis} onValueChange={emojis => change({ emojis })}/>
            Usar emoji nas mensagens principais
          </Row>
        </Card>

        <Card>
          <CardTitle>Mensagens</CardTitle>
          <Caption>Deixe em branco para usar a mensagem padrão do tom. As palavras entre chaves são trocadas na hora, como {'{cliente}'} pelo primeiro nome.</Caption>
          {EDITABLE_MESSAGES.map(m => (
            <Field key={m.key} as='div'>
              <Row $justify='space-between'>
                <span>{m.title}</span>
                {style.messages[m.key] && <LinkButton type='button' onClick={() => changeMessage(m.key, '')}>Usar o padrão</LinkButton>}
              </Row>
              <Hint>{m.when}{m.variables.length ? ` · variáveis: ${m.variables.map(v => `{${v}}`).join(' ')}` : ''}</Hint>
              <TextArea
                aria-label={m.title}
                rows={2}
                maxLength={MESSAGE_MAX_LENGTH}
                placeholder={defaultMessage(style.tone, m.key)}
                value={style.messages[m.key] ?? ''}
                onChange={e => changeMessage(m.key, e.target.value)}
              />
            </Field>
          ))}
        </Card>

        <Card>
          <CardTitle>Apelidos dos serviços</CardTitle>
          <Caption>Como as clientes chamam cada serviço (separe por vírgula). Ex.: "chapinha" para Escova.</Caption>
          {services.length === 0 && <Caption>Nenhum serviço aceita agendamento online ainda.</Caption>}
          {services.map(service => (
            <Field key={service.id}>
              {service.name}
              <input
                type='text'
                aria-label={`Apelidos de ${service.name}`}
                value={aliases[service.id] ?? ''}
                onChange={e => {
                  setAliases(current => ({ ...current, [service.id]: e.target.value }))
                  setSaved(false)
                }}
              />
            </Field>
          ))}
        </Card>

        {errors.map(e => <ErrorText key={e}>{e}</ErrorText>)}
        <Row>
          <MainButton label='Salvar' width={140} loading={saving} onClick={handleSave}/>
          {saved && <Caption>Salvo. Vale a partir da próxima mensagem.</Caption>}
        </Row>
      </Row>

      <Phone aria-label='Prévia da conversa'>
        <Caption style={{ textAlign: 'center' }}>Prévia</Caption>
        {preview.map((m, i) => <Bubble key={i} $mine={m.mine}>{m.text}</Bubble>)}
      </Phone>
    </StyleLayout>
  )
}
